import { Loader2, Check, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { SavingStatus } from '@/hooks/useSavingIndicator';

interface SaveStatusIndicatorProps {
  status: SavingStatus;
  error?: Error | null;
  className?: string;
}

/**
 * Visual indicator for saving state in quick action dropdowns.
 * Shows: nothing (idle), spinner (saving), checkmark (saved), or error icon.
 */
export function SaveStatusIndicator({ status, error, className }: SaveStatusIndicatorProps) {
  if (status === 'idle') {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-1.5 text-sm', className)}>
      {status === 'saving' && (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Saving...</span>
        </>
      )}
      
      {status === 'saved' && (
        <span className="flex items-center gap-1.5 text-primary">
          <Check className="h-3.5 w-3.5" />
          <span>Saved</span>
        </span>
      )}
      
      {status === 'error' && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 cursor-help">
                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                <span className="text-destructive">Failed</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{error?.message || 'An error occurred'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
